import { useState } from "react";
import { toast } from "sonner";
import { Mail, MapPin, Clock, Send, CalendarCheck, CheckCircle2 } from "lucide-react";
import { Link } from "react-router-dom";
import PublicPage from "@/components/PublicPage";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, apiError } from "@/lib/api";
import { COMPANY } from "@/lib/company";

const SUBJECTS = ["Product enquiry", "Request a quote", "Partnership", "Support", "Other"];
const BLANK = { name: "", company: "", email: "", phone: "", subject: "Product enquiry", message: "" };

export default function Contact() {
  const [form, setForm] = useState(BLANK);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/public/contact", form);
      setDone(data.reference);
      setForm(BLANK);
      toast.success("Enquiry sent — check your inbox for the acknowledgement");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Could not send right now");
    } finally {
      setBusy(false);
    }
  };

  return (
    <PublicPage
      testId="contact-page"
      title="Talk to us"
      updated={false}
      meta={{
        title: "Contact Plan-Sarthi — EPC Project Management Software India",
        description:
          "Talk to the Plan-Sarthi team about EPC project controls for your package. Email admin@plansarthi.com, registered office Ghazipur, Uttar Pradesh, or book a 30-minute live demo.",
        path: "/contact",
      }}
      intro="Tell us about the project you are executing and what your planning team is fighting with today. A person who knows EPC execution — not a chatbot — will reply."
    >
      <div className="grid lg:grid-cols-[1.3fr_1fr] gap-8">
        <section>
          {done ? (
            <div className="border border-emerald-500/40 bg-emerald-500/10 p-6" data-testid="contact-success">
              <CheckCircle2 className="w-6 h-6 text-emerald-500" />
              <h2 className="text-base md:text-lg font-semibold mt-3">Enquiry received</h2>
              <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
                Your reference is <strong className="num text-foreground" data-testid="contact-reference">{done}</strong>. We have emailed
                you an acknowledgement and our team is notified. We reply on working days, usually within one
                business day.
              </p>
              <div className="flex flex-wrap gap-2.5 mt-5">
                <Link to="/demo"><Button size="sm" data-testid="contact-book-demo-btn"><CalendarCheck className="w-3.5 h-3.5 mr-1.5" /> Book a live demo</Button></Link>
                <Button size="sm" variant="outline" onClick={() => setDone(null)} data-testid="contact-new-btn">Send another</Button>
              </div>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="c-name">Your name</Label>
                  <Input id="c-name" required minLength={2} value={form.name} onChange={set("name")} placeholder="R. Sharma" data-testid="contact-name-input" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="c-company">Company</Label>
                  <Input id="c-company" required minLength={2} value={form.company} onChange={set("company")} placeholder="Sharma Infra Pvt Ltd" data-testid="contact-company-input" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="c-email">Work email</Label>
                  <Input id="c-email" type="email" required value={form.email} onChange={set("email")} placeholder="you@company.com" data-testid="contact-email-input" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="c-phone">Phone (optional)</Label>
                  <Input id="c-phone" value={form.phone} onChange={set("phone")} placeholder="+91 98xxxxxxxx" data-testid="contact-phone-input" />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label>What is this about?</Label>
                <Select value={form.subject} onValueChange={(v) => setForm({ ...form, subject: v })}>
                  <SelectTrigger data-testid="contact-subject-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {SUBJECTS.map((s) => <SelectItem key={s} value={s} data-testid={`contact-subject-${s.replace(/\s/g, "-").toLowerCase()}`}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="c-message">Your message</Label>
                <Textarea
                  id="c-message"
                  required
                  minLength={10}
                  rows={6}
                  value={form.message}
                  onChange={set("message")}
                  placeholder="We are executing a 2×660 MW thermal package and our planning runs on 14 spreadsheets. We want the DPR to drive the S-curve automatically."
                  data-testid="contact-message-input"
                />
              </div>

              <Button type="submit" disabled={busy} className="w-full sm:w-auto" data-testid="contact-submit-btn">
                <Send className="w-3.5 h-3.5 mr-1.5" /> {busy ? "Sending…" : "Send enquiry"}
              </Button>
              <p className="text-[11.5px] text-muted-foreground">
                By sending this you agree to our{" "}
                <Link to="/privacy-policy" className="text-[#F97316] hover:underline">Privacy Policy</Link>. We use your
                details only to reply to you.
              </p>
            </form>
          )}
        </section>

        <aside className="space-y-4">
          <div className="border border-border bg-card p-5 space-y-4" data-testid="contact-details">
            <div className="flex items-start gap-2.5">
              <Mail className="w-4 h-4 text-[#F97316] mt-0.5 shrink-0" />
              <div>
                <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Email</div>
                <a href={`mailto:${COMPANY.email}`} className="text-[13px] hover:text-[#F97316]">{COMPANY.email}</a>
              </div>
            </div>
            <div className="flex items-start gap-2.5">
              <MapPin className="w-4 h-4 text-[#F97316] mt-0.5 shrink-0" />
              <div>
                <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Registered office</div>
                <div className="text-[13px]">{COMPANY.legalName}<br />{COMPANY.address}</div>
              </div>
            </div>
            <div className="flex items-start gap-2.5">
              <Clock className="w-4 h-4 text-[#F97316] mt-0.5 shrink-0" />
              <div>
                <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Working hours</div>
                <div className="text-[13px]">Mon – Sat · 9:30 AM – 6:30 PM IST</div>
              </div>
            </div>
          </div>

          <div className="border border-[#F97316]/40 bg-[#F97316]/5 p-5">
            <h3 className="font-semibold text-sm">Would you rather see it working?</h3>
            <p className="text-[12.5px] text-muted-foreground mt-2 leading-relaxed">
              Pick a 30-minute slot and we will walk through a live project package — DPR to S-curve to EVM — on
              your screen.
            </p>
            <Link to="/demo">
              <Button size="sm" className="mt-4 w-full" data-testid="contact-sidebar-demo-btn">
                <CalendarCheck className="w-3.5 h-3.5 mr-1.5" /> Book a live demo
              </Button>
            </Link>
          </div>
        </aside>
      </div>
    </PublicPage>
  );
}
